import React, { useState, useEffect } from 'react';
import { Business, Service, User } from '../../types';
import { Building2, MapPin, Phone, Paperclip, CheckCircle, Info, X } from 'lucide-react';
import type { QuoteRequestEntry } from './BookingStepQuote';
import { QUOTE_REQUEST_ALL_KEY } from './BookingStepQuote';

interface ThemeStyles {
  pageBg?: string;
  cardBg?: string;
  textPrimary?: string;
  textSecondary?: string;
  border?: string;
  buttonPrimary?: string;
  inlineStyles?: {
    buttonPrimary?: React.CSSProperties;
  };
}

interface BookingStepQuoteReviewProps {
  business: Business;
  quotableServices: Service[];
  nonQuotableServices?: Service[];
  quoteRequests: { [serviceId: string]: QuoteRequestEntry };
  preferredScheduleText?: string;
  selectedProfessionals: { [serviceId: string]: User | undefined };
  themeStyles?: ThemeStyles;
}

const formatPrice = (price: number | null) => {
  if (price == null || !Number.isFinite(Number(price))) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(price));
};

const getProfessionalDisplayName = (professional: User) => {
  if (professional.firstName && professional.lastName) {
    return `${professional.firstName} ${professional.lastName.charAt(0)}.`;
  }
  return professional.email || 'Any';
};

const BookingStepQuoteReview: React.FC<BookingStepQuoteReviewProps> = ({
  business,
  quotableServices,
  nonQuotableServices = [],
  quoteRequests,
  preferredScheduleText = '',
  selectedProfessionals,
  themeStyles
}) => {
  const primaryColor = themeStyles?.inlineStyles?.buttonPrimary?.backgroundColor || themeStyles?.buttonPrimary || '#3B82F6';
  const cardBg = themeStyles?.cardBg ?? '#ffffff';
  const borderColor = themeStyles?.border ?? '#e5e7eb';
  const textColor = themeStyles?.textPrimary ?? '#111827';
  const mutedColor = themeStyles?.textSecondary ?? '#6b7280';

  const allEntry = quoteRequests[QUOTE_REQUEST_ALL_KEY];
  const imageUrlsList = allEntry?.imageUrls ?? [];
  const previewUrlsList = allEntry?.previewUrls ?? [];
  const attachmentIdsList = allEntry?.attachmentIds ?? [];
  const totalImageCount = Math.max(imageUrlsList.length, attachmentIdsList.length, previewUrlsList.length);
  const thumbnailUrls = previewUrlsList.length >= totalImageCount
    ? previewUrlsList
    : imageUrlsList.length > 0
      ? imageUrlsList
      : attachmentIdsList.map(() => null);
  const detailsText = (allEntry?.details ?? '').trim();

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxUrl(null);
    };
    if (lightboxUrl) {
      document.addEventListener('keydown', onEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', onEscape);
      document.body.style.overflow = '';
    };
  }, [lightboxUrl]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2" style={{ color: textColor }}>
          Review Your Quote Request
        </h2>
        <p className="text-base" style={{ color: mutedColor }}>
          Please confirm the information below before submitting your request.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-stretch">
        {/* Left Column */}
        <div className="w-full lg:w-1/2 flex flex-col gap-6 self-stretch">
          {/* Business Information */}
          <div
            className="rounded-xl border p-6 flex flex-col flex-1"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Building2 className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-xl font-bold" style={{ color: textColor }}>
                Business Information
              </h3>
            </div>
            <p className="text-sm mb-4" style={{ color: mutedColor }}>
              This quote request will be sent to the business for review.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0 text-sm font-semibold"
                  style={{ backgroundColor: `${borderColor}`, color: mutedColor }}
                >
                  {business.name?.charAt(0)?.toUpperCase() ?? 'B'}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>
                    Business Name
                  </span>
                  <p className="text-base font-medium mt-1" style={{ color: textColor }}>
                    {business.name || business.shortName || 'Business'}
                  </p>
                </div>
              </div>
              {business.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: mutedColor }} />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>
                      Address
                    </span>
                    <p className="text-base mt-1" style={{ color: textColor }}>
                      {business.address}
                    </p>
                  </div>
                </div>
              )}
              {business.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 flex-shrink-0" style={{ color: mutedColor }} />
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>
                      Phone
                    </span>
                    <p className="text-base mt-1" style={{ color: textColor }}>
                      {business.phone}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Attachments */}
          <div
            className="rounded-xl border p-6 flex flex-col"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Paperclip className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-xl font-bold" style={{ color: textColor }}>
                Attachments
              </h3>
            </div>
            <p className="text-sm mb-4" style={{ color: mutedColor }}>
              Optional images provided to help the business prepare your quote.
            </p>
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>
              Images (optional)
            </span>
            <div className="mt-3">
              {totalImageCount > 0 ? (
                <>
                  <p className="text-sm mb-2" style={{ color: mutedColor }}>
                    {totalImageCount} image{totalImageCount !== 1 ? 's' : ''} attached
                  </p>
                  <div
                    className="grid grid-cols-2 gap-2 w-full md:flex md:flex-wrap md:gap-3"
                    style={{ maxWidth: '100%' }}
                  >
                    {thumbnailUrls.map((url, index) => (
                      <button
                        key={`${index}-${url || 'pl'}`}
                        type="button"
                        className="relative rounded-lg overflow-hidden border shadow-sm w-full aspect-square min-w-0 md:w-[88px] md:h-[88px] md:aspect-auto md:flex-shrink-0 cursor-pointer p-0 text-left block"
                        style={{
                          borderColor,
                          backgroundColor: url ? 'transparent' : `${borderColor}40`
                        }}
                        onClick={() => url && setLightboxUrl(url)}
                        disabled={!url}
                        aria-label={`View image ${index + 1}`}
                      >
                        {url ? (
                          <img
                            src={url}
                            alt={`Attachment ${index + 1}`}
                            className="w-full h-full object-cover absolute inset-0"
                            onError={(e) => {
                              const el = e.target as HTMLImageElement;
                              el.style.display = 'none';
                              const parent = el.closest('.relative');
                              const fallback = parent?.querySelector('[data-review-thumb-fallback]') as HTMLElement;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          data-review-thumb-fallback
                          className="w-full h-full flex items-center justify-center absolute inset-0"
                          style={{
                            color: mutedColor,
                            display: url ? 'none' : 'flex',
                            backgroundColor: `${borderColor}40`
                          }}
                        >
                          <Paperclip className="w-6 h-6 opacity-50" />
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <span className="text-sm" style={{ color: mutedColor }}>
                  No images attached
                </span>
              )}
            </div>
          </div>

          {/* Lightbox overlay */}
          {lightboxUrl && (
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
              onClick={() => setLightboxUrl(null)}
              role="dialog"
              aria-modal="true"
              aria-label="Image full size view"
            >
              <button
                type="button"
                onClick={() => setLightboxUrl(null)}
                className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={lightboxUrl}
                alt="Full size"
                className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="w-full lg:w-1/2 flex flex-col gap-6 self-stretch">
          {/* Quote Requests */}
          <div
            className="rounded-xl border p-6 flex flex-col flex-1"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <CheckCircle className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-xl font-bold" style={{ color: textColor }}>
                Quote Requests
              </h3>
            </div>
            <p className="text-sm mb-4" style={{ color: mutedColor }}>
              Services and details for this quote request.
            </p>
            {/* Quotable services (names + tags only) */}
            <div className="space-y-2 mb-4">
              {quotableServices.map((service) => {
                const raw = (service as any).serviceLocation || (service as any).service_location;
                const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
                const isOnSite = loc === 'ON_SITE' || loc === 'ONSITE';
                const professional = selectedProfessionals[service.id];
                return (
                  <div
                    key={service.id}
                    className="flex flex-wrap items-center justify-between gap-2 py-2 border-b last:border-b-0"
                    style={{ borderColor: `${borderColor}60` }}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="text-sm font-semibold" style={{ color: textColor }}>
                        {service.name}
                      </h4>
                      {professional && (
                        <span className="text-xs" style={{ color: mutedColor }}>
                          Professional: {getProfessionalDisplayName(professional)}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {isOnSite && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                          style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', color: '#059669' }}
                        >
                          On-site
                        </span>
                      )}
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
                      >
                        Quotable
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Single combined details */}
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>
                Details
              </span>
              <p className="text-sm mt-1 whitespace-pre-wrap" style={{ color: textColor }}>
                {detailsText || '—'}
              </p>
            </div>
            {preferredScheduleText?.trim() && (
              <div className="pt-4 border-t mt-4" style={{ borderColor }}>
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>
                  Preferred schedule
                </span>
                <p className="text-sm mt-1 whitespace-pre-wrap" style={{ color: textColor }}>
                  {preferredScheduleText.trim()}
                </p>
              </div>
            )}

            {nonQuotableServices.length > 0 && (
              <div className="pt-4 border-t mt-4" style={{ borderColor }}>
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: mutedColor }}>
                  Other services (included in this request)
                </span>
                <ul className="mt-2 space-y-2">
                  {nonQuotableServices.map((service) => (
                    <li
                      key={service.id}
                      className="flex justify-between items-baseline gap-2"
                      style={{ color: textColor }}
                    >
                      <span className="text-sm font-medium">{service.name}</span>
                      <span className="text-sm font-semibold flex-shrink-0">
                        {formatPrice(service.price)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Info box */}
          <div
            className="rounded-xl border p-4 flex gap-3"
            style={{ backgroundColor: `${primaryColor}08`, borderColor: `${primaryColor}30` }}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
            >
              <Info className="w-4 h-4" />
            </div>
            <p className="text-sm" style={{ color: textColor }}>
              This is a quote request (not a confirmed booking). The business will review your details and respond
              with an offer or follow-up questions if needed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingStepQuoteReview;
